import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MultiSelectComponent } from '@syncfusion/ej2-angular-dropdowns';
import {  MultiDropDown } from 'src/app/interfaces/drop-down.interface';
import { LocationService } from 'src/app/services/location.service';
import { LogService } from 'src/app/services/log.service';
import { DropDownTreeComponent } from '@syncfusion/ej2-angular-dropdowns';
import { DropDown } from 'src/app/interfaces/drop-down.interface';

interface TreeNode {
  id: string;
  pid: string | null;
  text: string;
  hasChildren?: boolean;
  expanded?: boolean;
  selectable?: boolean;
  value?: string;
  child?: TreeNode[];
}

export const LOCATION_FORM_CONTROL_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectLocationFormFieldComponent),
  multi: true,
};

@Component({
  selector: 'app-multi-select-location-form-field',
  providers: [LOCATION_FORM_CONTROL_ACCESSOR],
  templateUrl: './multi-select-location-form-field.component.html',
  styleUrls: ['./multi-select-location-form-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectLocationFormFieldComponent 
  implements ControlValueAccessor, OnChanges, AfterViewInit
{
  @ViewChild('locationDropDownTree') dropDownTree: DropDownTreeComponent;
  @ViewChild('locationTreeWrapper', { read: ElementRef }) locationTreeElementRef: ElementRef;
  
  @Input() orgId: number[] = [];
  @Input() includeChildOrgs: boolean = false;
  @Input() placeholder: string = 'General.SelectLocation';
  @Input() disabledLocationIds: number[] = [];

  options: MultiDropDown<string>[] = null;
  private _value: MultiDropDown<string>[] = [];
  isLoading = false;
  loading: boolean = false;
  previousOrgIds: number[] = [];
  private dataLoaded = false;

  public treeData:TreeNode[] = [];
  _disabledSet : Set<string>;

  get treeFields(): any {
    return {
      dataSource: this.treeData,
      id: 'id',
      parentValue: 'pid',
      text: 'text',
      hasChildren: 'hasChildren',
      value: 'value'
    };
  }

  public watermark: string = 'Select a Location';
  public showCheckBox = true;
  public mode = "Custom";
  public treeSettings: Object = {  autoCheck: false,
  loadOnDemand: false };
  public filterType: string = 'Contains';
  public locationNodeIds: string[] = [];
  public selectedLocationIds: string[] = [];
  public enabledSelectedCount = 0;
  private nodeMap: Map<string, TreeNode> = new Map();
  private parentToChildrenMap: Map<string, string[]> = new Map();
  private childToParentMap: Map<string, string> = new Map();

  private _disabled: boolean = false;
  public get disabled(): boolean {
    return this._disabled;
  }
  @Input() public set disabled(value: boolean) {
    this._disabled = value;
  }

  
getEnabledSelectedCount(): number {
  return this.selectedLocationIds.filter(id => !this.isReadonly(id)).length;
}

private get treeViewObj() {
  return (this.dropDownTree as any)?.treeObj;
}


private updateEnabledSelectedCount(): void {
  this.enabledSelectedCount = this.selectedLocationIds.filter(id => !this.isReadonly(id)).length;
}

  constructor(
    private _ls: LocationService,
    private _lgr: LogService,
    private _cdr: ChangeDetectorRef,
  ) {}
  private clearIconListener: EventListener;

  ngAfterViewInit(): void {
    this.updateLocationFieldValue();
  }

  private preserveDisabledCheckedNodes(treeObj: any): void {
  if (!this.disabledLocationIds || this.disabledLocationIds.length === 0) {
    // No disabled nodes to preserve, so clear everything
    treeObj.checkedNodes = [];
    treeObj.value = [];
    treeObj.dataBind();

    this.selectedLocationIds = [];
    this.onChange([]);
    this._cdr.detectChanges();
    return;
  }

  // Otherwise, preserve checked disabled nodes as before
  const disabledChecked = this.treeData
    .filter(node =>
      node.id.startsWith('loc_') &&
      this.disabledLocationIds.includes(Number(node.id.replace('loc_', '')))
    )
    .map(node => node.id);

  treeObj.checkedNodes = disabledChecked;
  treeObj.value = disabledChecked;
  treeObj.dataBind();

  this.selectedLocationIds = [];
  this.onChange([]);
  this._cdr.detectChanges();
}

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['orgId']) {
      const currentIds = this.orgId || [];
      if (!currentIds.length || this.arraysEqual(this.previousOrgIds, currentIds)) {
        return;
      }
      this.previousOrgIds = [...currentIds];
      //this.selectedLocationIds = [];
      this.treeData = [];
      this.locationNodeIds = [];
      this.loadLocationData();
    }
  }

  private arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
}

  // Function to call when the input changes.
  onChange = (value: DropDown<string>[]| null) => {
  };

  // Function to call when the input is touched.
  onTouched = () => {};

  onLocationTreeChange(event: any): void {

  if (this.updatingSelection) return;
  
  const treeViewObj = this.treeViewObj;
  const checkedNodeIds = new Set(treeViewObj?.getAllCheckedNodes?.() ?? []);
  // Get newly checked/unchecked node
  const changedNodeId = event.data?.id;
  const isChecked = event.checked; // true = checked, false = unchecked

  // Custom logic: if a parent was checked, check all its children
  if (isChecked && this.isParentNode(changedNodeId)) {
    const childNodeIds = this.getChildNodeIds(changedNodeId);
    for (const childId of childNodeIds) {
      const isDisabled = childId.startsWith('loc_') && this.disabledLocationIds.includes(Number(childId.replace('loc_', '')));
      if (!isDisabled) {
        checkedNodeIds.add(childId);
      }
    }
  }

  // If a parent was unchecked, uncheck all its children
  if (!isChecked && this.isParentNode(changedNodeId)) {
     const childNodeIds = this.getChildNodeIds(changedNodeId);

  for (const childId of childNodeIds) {
    const isDisabled = childId.startsWith('loc_') && this.disabledLocationIds.includes(Number(childId.replace('loc_', '')));
    if (!isDisabled) {
      checkedNodeIds.delete(childId);
    }
  }
  }

  // Sync back the checked nodes
  treeViewObj.checkedNodes = Array.from(checkedNodeIds);
  treeViewObj.dataBind();

  // Filter out disabled tank node IDs
  const enabledTankIds: string[] = Array.from(checkedNodeIds as Set<string>).filter((id: string) => {
    const isTank = id.startsWith('loc_');
    if (!isTank) return true;
    const tankIdNum = Number(id.replace('loc_', ''));
    return !this.disabledLocationIds.includes(tankIdNum);
  });

  this.selectedLocationIds = enabledTankIds.filter((id: string) =>
    this.locationNodeIds.includes(id)
  );

  this.selectedLocationIds = [...this.selectedLocationIds];

  const selectedValues: DropDown<string>[] = this.getSelectedDropDownsFromIds(this.selectedLocationIds);

  this.updateParentSelection(treeViewObj, changedNodeId);
  
  this.onTouched();
  this.onChange(selectedValues);

    this.selectedLocationIds = this.selectedLocationIds.filter(id =>
      this.locationNodeIds.includes(id) && !this.isReadonly(id)
    );

    this.updateEnabledSelectedCount();
    this.updateDisplayText();
    this._cdr.markForCheck();
    this._cdr.detectChanges();
}

private isParentNode(nodeId: string): boolean {
  return this.parentToChildrenMap.has(nodeId);
}
private getChildNodeIds(parentId: string): string[] {
  return this.parentToChildrenMap.get(parentId) || [];
}

  private getSelectedDropDownsFromIds(ids: string[]): DropDown<string>[] {
  return ids
    .map(id => {
      const node = this.nodeMap.get(id);
      if (node && node.selectable && id.startsWith('loc_')) {
        const organization = node.pid ? this.nodeMap.get(node.pid) : null;
        return {
          text: node.text,
          value: id.replace('loc_', ''),
          parentValue: node.pid?.replace('org_', ''),
          parentText: organization?.text,
        };
      }
      return null;
    })
    .filter(Boolean);
}

  private loadLocationData(): void {
  this.loading = true;
  this._cdr.markForCheck();

  // ✅ Step 1: Save currently selected location IDs
  const previouslySelected = [...this.selectedLocationIds];

  this._ls.getLocationsForDropDownByOrgIds(this.orgId, this.includeChildOrgs).subscribe((tanks) => {
    this.treeData = this.buildFlatTree(tanks);
    this.dataLoaded = true;
    this.locationNodeIds = this.getAllLocationNodeIds();
    this.loading = false;

    // ✅ Step 2: Filter out only those that still exist
    const newValidSelected = previouslySelected.filter(id =>
      this.locationNodeIds.includes(id)
    );

    this.selectedLocationIds = [...newValidSelected];

    // ✅ Step 3: Update model (_value) based on filtered selected IDs
    this._value = this.getSelectedDropDownsFromIds(this.selectedLocationIds);

    // ✅ Step 4: Update display
    setTimeout(() => {
      this.updateLocationFieldValue();

      if (this.dropDownTree) {
        this.dropDownTree.dataBind();
        (this.dropDownTree as any).updateInput?.(true);
        (this.dropDownTree as any).refreshInput?.(); // New
        this.updateDisplayText();
      }
      this.onDataBound();
      this.onChange(this._value); // Update form value
      
      this.updateEnabledSelectedCount();
      this._cdr.markForCheck();
      this._cdr.detectChanges();
    });
  });
}

onDataBound(): void {
  setTimeout(() => {
    
    const treeViewObj = this.treeViewObj;
    if (!treeViewObj || !this.treeData?.length) return;

    const disabledSet = new Set<string>();

    // Step 1: Collect disabled location node IDs
    const disabledLocationNodeIds = this.treeData
      .filter(node =>
        node.id.startsWith('loc_') &&
        this.disabledLocationIds.includes(Number(node.id.replace('loc_', '')))
      )
      .map(node => node.id);

    // Add to disabled set
    disabledLocationNodeIds.forEach(id => disabledSet.add(id));

    // Step 2: Disable orgs if all their tanks are disabled
    const orgNodes = this.treeData.filter(node => node.id.startsWith('org_'));
    for (const org of orgNodes) {
      const childLocs = this.treeData.filter(n => n.pid === org.id && n.id.startsWith('loc_'));
      if (childLocs.length && childLocs.every(child => disabledSet.has(child.id))) {
        disabledSet.add(org.id);
      }
    }
   

    // ✅ Step 3: Build final checked set
    const selectedIdsFromWriteValue = (this._value ?? []).map(v => `loc_${v.value}`);
    const allValidIds = new Set(this.treeData.map(n => n.id));
    const manuallyIncludeDisabledIds = disabledLocationNodeIds.filter(id => allValidIds.has(id));

    const finalCheckSet = new Set<string>([
      ...this.selectedLocationIds,
      ...selectedIdsFromWriteValue,
      ...manuallyIncludeDisabledIds,
    ]);

    // Only keep valid node IDs from the tree
    const allIdsSet = new Set(this.nodeMap.keys());
    const validCheckedNodes = [...finalCheckSet].filter(id => allIdsSet.has(id));

    // Step 4: Apply checked nodes
    treeViewObj.checkedNodes = validCheckedNodes;
    treeViewObj.dataBind();

    // Step 5: Disable nodes
    if (treeViewObj.disableNodes && disabledSet.size > 0) {
      treeViewObj.disableNodes(Array.from(disabledSet));
    }

    this._disabledSet = disabledSet;

    this.selectedLocationIds = validCheckedNodes.filter(id =>
      id.startsWith('loc_') &&
      !this.disabledLocationIds.includes(Number(id.replace('loc_', '')))
    );

    this.selectedLocationIds = [...this.selectedLocationIds];
    //this.updateCustomSelectionText();
    setTimeout(() => {
      this._cdr.detectChanges();
    });
  });
}

private updateDisplayText(): void {
  const input: HTMLInputElement = this.locationTreeElementRef?.nativeElement.querySelector('input');

  if (!input) return;

  const enabledSelectedIds = this.selectedLocationIds.filter(id => !this.isReadonly(id));
  const locationCount = enabledSelectedIds.length;

  const displayText = locationCount > 0
    ? `${locationCount} item${locationCount > 1 ? 's' : ''} selected`
    : '';

  input.value = displayText;
  input.setAttribute('value', displayText);

  // Also update the internal DropDownTree display (Syncfusion control)
  const dropDownTree = (this.dropDownTree as any);
  dropDownTree?.updateInput?.(true);
  dropDownTree?.refreshInput?.();

  this._cdr.detectChanges();
}

private buildFlatTree(data: any[]): TreeNode[] {
  const flat: TreeNode[] = [];

  this.nodeMap.clear();
  this.parentToChildrenMap.clear();
  this.childToParentMap.clear();

  data.forEach((item) => {
    const orgId = `org_${item.orgId}`;
    const locId = `loc_${item.locationId}`;

    if (!this.nodeMap.has(orgId)) {
      const orgNode: TreeNode = {
        id: orgId,
        pid: null,
        text: item.orgName,
        hasChildren: true,
        value: orgId,
        expanded: true,
        selectable: true
      };
      flat.push(orgNode);
      this.nodeMap.set(orgId, orgNode);
    }

    const locNode: TreeNode = {
      id: locId,
      pid: orgId,
      text: item.locationName,
      hasChildren: false,
      selectable: true,
      value: locId,
      expanded: true
    };

    flat.push(locNode);
    this.nodeMap.set(locId, locNode);

    // Update child->parent and parent->child maps
    this.childToParentMap.set(locId, orgId);

    if (!this.parentToChildrenMap.has(orgId)) {
      this.parentToChildrenMap.set(orgId, []);
    }

    this.parentToChildrenMap.get(orgId).push(locId);
  });

  return flat;
}

private getAllLocationNodeIds(): string[] {
  const ids: string[] = [];
  this.nodeMap.forEach((node, id) => {
    if (node.selectable && id.startsWith('loc_')) {
      ids.push(id);
    }
  });
  return ids;
}

  // Update the model and changes needed for the view here.
  writeValue(value: MultiDropDown<string>[]): void {
     if (value) {
        this._value = value.map(v => ({
          ...v,
          value: String(v.value),
        }));
      this.selectedLocationIds = this._value.map(v => `loc_${v.value}`);
      } else {
        this._value = [];
        this.selectedLocationIds = [];
      }
      this.updateEnabledSelectedCount();
      this._cdr.markForCheck();
      //this.updateCustomSelectionText();
      if (this.options?.length) {
        this.updateLocationFieldValue();
      }
      if (value !== null) {
        this.onChange(value);
      }
    //this.onChange(value);
    // ✅ Force refresh of the customTemplate in input
    setTimeout(() => {
      this.dropDownTree?.dataBind();
      (this.dropDownTree as any).updateInput?.(true); // <== Key line
      (this.dropDownTree as any).refreshInput?.(); // New
      this.updateDisplayText();
    }, 0);
  }

  private updateLocationFieldValue() {
  if (this.dropDownTree) {
    this.dropDownTree.value = [...this.selectedLocationIds];
    this.dropDownTree.dataBind();

    // ✳️ Force re-render input text manually if needed
    (this.dropDownTree as any).updateInput?.(true);
    (this.dropDownTree as any).refreshInput?.(); // New

    // Fallback: manually clear native input (in case Syncfusion caches it)
    const input: HTMLInputElement = this.locationTreeElementRef?.nativeElement.querySelector('input');
    if (input) {
      input.value = '';
    }
  }

  setTimeout(() => {
    this._cdr.detectChanges();
  }, 0);
}

  // Allows Angular to register a function to call when the model (organizationControl) changes.
  // Save the function as a property to call later here.
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Allows Angular to register a function to call when the input has been touched.
  // Save the function as a property to call later here.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  onFiltering(event: any): void {
  // Optional: can customize filtering behavior if needed
    // For now, allow default filtering

    const treeViewObj = this.treeViewObj;

    if (treeViewObj) {
      // Reapply the already selected locations as checked
      treeViewObj.checkedNodes = [...this.selectedLocationIds];
      treeViewObj.dataBind();
    }

    // Keep count correct
    this.updateDisplayText();
}

onInput(event: any): void {
  const inputValue = event?.target?.value?.trim() ?? '';

  if (!inputValue) {
    // Filter cleared – force checked node sync
    setTimeout(() => {
      const treeViewObj = this.treeViewObj;

      if (treeViewObj) {
        //const currentChecked = treeViewObj.getAllCheckedNodes(); // <== KEY FIX

        const currentChecked = new Set(this.selectedLocationIds);

        // Restore only allowed nodes
        const restoredChecked = this.treeData
          .filter(node => node.id.startsWith('loc_') && currentChecked.has(node.id))
          .map(node => node.id);

        // Reassign checked nodes to force re-render
        treeViewObj.checkedNodes = restoredChecked;
        treeViewObj.dataBind();
      }
    },0);
  }
}

private handlingNodeSelect = false;

  onNodeSelect(event: any): void {
    // Writing `checkedNodes` raises `select` once per checked node, so a single
    // programmatic re-sync fired this handler once per selected location.
    // Syncfusion flags those as not interacted; only a real click is ours to handle.
    if (!event?.isInteracted) return;
    if (this.updatingSelection || this.handlingNodeSelect) return;

    this.handlingNodeSelect = true;

    try {
      const treeViewObj = this.treeViewObj;
      if (!event?.itemData || !treeViewObj) return;

      const nodeData = event.itemData;
      const nodeId: string = nodeData.id;
      const isChecked: boolean = nodeData.isChecked === 'true';
      const hasChildren: boolean = nodeData.hasChildren;

      // Built once per event. These were array scans run per selected id below,
      // which made each pass cost selection x tree size.
      const disabledSet = new Set<number>(this.disabledLocationIds ?? []);
      const locationNodeIdSet = new Set<string>(this.locationNodeIds);
      const isDisabledLocation = (id: string) =>
        id.startsWith('loc_') && disabledSet.has(Number(id.replace('loc_', '')));

      // ✅ Handle child selection for parent nodes
      if (hasChildren) {
        const childNodeIds = this.getChildNodeIds(nodeId);
        const checkedSet = new Set<string>(this.selectedLocationIds);

        for (const childId of childNodeIds) {
          const isDisabled = isDisabledLocation(childId);
          if (isChecked) {
            checkedSet.add(childId);
          } else if (!isDisabled) {
            checkedSet.delete(childId);
          }
        }

        this.selectedLocationIds = [...checkedSet];
        treeViewObj.checkedNodes = [...this.selectedLocationIds];
        treeViewObj.dataBind();
      } else {
        // ✅ Handle single node select/unselect
        if (isChecked) {
          if (!this.selectedLocationIds.includes(nodeId)) {
            this.selectedLocationIds.push(nodeId);
          }
        } else {
          this.selectedLocationIds = this.selectedLocationIds.filter(id => id !== nodeId);
        }
      }

      // ✅ Always filter to valid, enabled "loc_" IDs
      this.selectedLocationIds = this.selectedLocationIds.filter(
        (id: string) => !isDisabledLocation(id) && locationNodeIdSet.has(id)
      );

      // ✅ Notify model/form
      this.onChange(this.getSelectedDropDownsFromIds(this.selectedLocationIds));
      this.onTouched();

      // ✅ Update parent node selection if needed
      this.updateParentSelection(treeViewObj, nodeId);

      // ✅ Refresh display text only. The checked state was already written above;
      // re-writing a narrower set here made the tree raise `select` again after the
      // re-entrancy flag had been released, so the handler kept re-triggering
      // itself, and it dropped the parent node updateParentSelection had just added.
      this.updateEnabledSelectedCount();
      setTimeout(() => {
        this.updateDisplayText();  // <-- count always based on selectedLocationIds
        treeViewObj.updateInput?.(true);
        treeViewObj.refreshInput?.();
        this._cdr.markForCheck();
        this._cdr.detectChanges();
      }, 10);

    } finally {
      this.handlingNodeSelect = false;
    }
  }

private updatingSelection = false;

private updateParentSelection(treeViewObj: any, changedNodeId: string): void {
  if (this.updatingSelection) return;

  this.updatingSelection = true;

  try {
    const parentNodeId = this.childToParentMap.get(changedNodeId);
    if (!parentNodeId) return;

    const siblingIds = this.parentToChildrenMap.get(parentNodeId) || [];
    const checkedNodes = new Set(treeViewObj.getAllCheckedNodes());

    const allSiblingsChecked = siblingIds.every(childId =>
      this.disabledLocationIds.includes(Number(childId.replace('loc_', ''))) ||
      checkedNodes.has(childId)
    );

    if (allSiblingsChecked) {
      checkedNodes.add(parentNodeId);
    } else {
      checkedNodes.delete(parentNodeId);
    }

    treeViewObj.checkedNodes = Array.from(checkedNodes);
    treeViewObj.dataBind();
  } finally {
    this.updatingSelection = false;
  }
}

onCustomClear(): void {
  const ddTree = this.dropDownTree;
  const treeObj = (ddTree as any)?.treeObj;
  if (!treeObj) return;

  // Clear DropDownTree UI
  ddTree.clear();

  // Clear all tree-related selections
  treeObj.clearCheckedNodes?.();
  treeObj.clearSelection?.();
  treeObj.value = [];
  treeObj.checkedNodes = [];
  treeObj.selectedNodes = [];

  treeObj.dataBind();

  // Reset component state
  this.selectedLocationIds = [];
  this._value = [];

  // Notify form control
  this.onChange([]);
  this.onTouched();

  // Clear input field
  const input: HTMLInputElement = this.locationTreeElementRef?.nativeElement.querySelector('input');
  if (input) input.value = '';

  // Re-bind disabled checked nodes (restore read-only selections if any)
  setTimeout(() => {
    this.restoreDisabledCheckedNodes(treeObj);
    ddTree.dataBind();
    this.updateEnabledSelectedCount();
    this.updateDisplayText();
    this._cdr.markForCheck();
    //this.updateDisplayText();  // 👈 Update input display
    this._cdr.detectChanges();
  }, 0);
}

isReadonly(id: string): boolean {
  if (!id.startsWith('loc_')) return false;
  const numericId = Number(id.replace('loc_', ''));
  return this.disabledLocationIds.includes(numericId);
}

private restoreDisabledCheckedNodes(treeObj: any): void {
  if (!this.disabledLocationIds?.length) return;

  const disabledChecked = this.treeData
    .filter(node =>
      node.id.startsWith('loc_') &&
      this.disabledLocationIds.includes(Number(node.id.replace('loc_', '')))
    )
    .map(node => node.id);

  // Restore disabled selections
  treeObj.checkedNodes = disabledChecked;
  treeObj.value = disabledChecked;
  treeObj.selectedNodes = disabledChecked;
  treeObj.dataBind();

  this._cdr.detectChanges();
}

}